import { filter, map, Subject } from 'rxjs';
import { PulseDocument, PulseEnvironment } from '../../../globals';
import { PulseBroadcastChannel } from '../../broadcast';

export class CookieChangeDetector {
    private static patchedDocuments = new Map<PulseDocument, Subject<boolean>>();

    constructor(
        cookieBroadcastChannel: PulseBroadcastChannel,
        private env: PulseEnvironment,
    ) {
        const stream = this.patchChanges();

        stream.pipe(filter((external) => !external)).subscribe(() => {
            cookieBroadcastChannel.postMessage(true);
        });

        cookieBroadcastChannel.messages.pipe(map(() => true)).subscribe(stream);
    }

    /**
     * Emits true when the change is external and
     * false if the change is internal!
     */
    changes(): Subject<boolean> {
        return this.patchChanges();
    }

    private patchChanges(): Subject<boolean> {
        const doc = this.env.doc;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let stream = CookieChangeDetector.patchedDocuments.get(doc)!;

        if (stream) {
            return stream;
        }

        stream = new Subject();
        CookieChangeDetector.patchedDocuments.set(doc, stream);
        const docPrototype = this.env.win.Document.prototype;
        const nativeCookieDesc = Object.getOwnPropertyDescriptor(docPrototype, 'cookie');

        let lastCookie = doc.cookie;
        const descriptor: PropertyDescriptor = {
            enumerable: true,
            configurable: true,
            get() {
                return doc.__native_cookie;
            },
            set: (value) => {
                doc.__native_cookie = value;
                const cookie = doc.__native_cookie;

                if (cookie !== lastCookie) {
                    try {
                        stream.next(false);
                    } finally {
                        lastCookie = cookie;
                    }
                }
            },
        };

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        Object.defineProperty(docPrototype, '__native_cookie', nativeCookieDesc!);
        Object.defineProperty(docPrototype, 'cookie', descriptor);

        return stream;
    }
}
